// Utility for tracking visitors without any database or Supabase server.

const COUNTER_API_URL = "https://api.counterapi.dev/v1/jpg_png_converter_app_2026/visits";

/**
 * Parses user agent to extract basic browser and OS information
 */
export function getBrowserAndOS() {
  const ua = navigator.userAgent;
  let browser = "Unknown Browser";
  let os = "Unknown OS";

  // Detect OS
  if (ua.includes("Win")) os = "Windows";
  else if (ua.includes("Mac")) os = "macOS";
  else if (ua.includes("Linux")) os = "Linux";
  else if (ua.includes("Android")) os = "Android";
  else if (ua.includes("like Mac")) os = "iOS";

  // Detect Browser
  if (ua.includes("Firefox")) browser = "Firefox";
  else if (ua.includes("SamsungBrowser")) browser = "Samsung Internet";
  else if (ua.includes("Opera") || ua.includes("OPR")) browser = "Opera";
  else if (ua.includes("Trident")) browser = "Internet Explorer";
  else if (ua.includes("Edge") || ua.includes("Edg")) browser = "Edge";
  else if (ua.includes("Chrome")) browser = "Chrome";
  else if (ua.includes("Safari")) browser = "Safari";

  const device = /Mobile|Android|iP(hone|od|ad)/i.test(ua) ? "Mobile" : "Desktop";

  return { browser, os, device };
}

/**
 * Tracks visitor on page load
 */
export async function trackVisit() {
  try {
    // 1. Get or create unique visitor token
    let visitorId = localStorage.getItem("visitor_id");
    let isNewVisitor = false;
    if (!visitorId) {
      visitorId = "vis_" + Math.random().toString(36).substring(2, 11) + "_" + Date.now();
      localStorage.setItem("visitor_id", visitorId);
      isNewVisitor = true;
    }

    // 2. Local visits count
    let localVisits = parseInt(localStorage.getItem("my_visit_count") || "0", 10) + 1;
    localStorage.setItem("my_visit_count", localVisits.toString());

    // 3. Visitor log details
    const { browser, os, device } = getBrowserAndOS();
    const currentSession = {
      visitorId,
      timestamp: new Date().toISOString(),
      browser,
      os,
      device,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      isNewVisitor,
      visitCount: localVisits,
    };

    // Store in localStorage history of visits for dashboard analytics
    const existingLogs = JSON.parse(localStorage.getItem("visitor_logs") || "[]");
    // Prepend new log and keep top 50
    const updatedLogs = [currentSession, ...existingLogs].slice(0, 50);
    localStorage.setItem("visitor_logs", JSON.stringify(updatedLogs));

    // Update global visits via Counter API (database-free public counter)
    try {
      fetch(`${COUNTER_API_URL}/up`).catch(() => {});
    } catch (e) {
      // Ignore API errors gracefully
    }

    return currentSession;
  } catch (err) {
    console.error("Error tracking visit:", err);
  }
}

/**
 * Gets visitor statistics for admin display without any database
 */
export async function getVisitorStats() {
  let totalPageViews = 0;
  
  // Try fetching total global views from Counter API
  try {
    const res = await fetch(COUNTER_API_URL);
    if (res.ok) {
      const data = await res.json();
      totalPageViews = data.count || 0;
    }
  } catch (err) {
    // Fallback calculation from local storage if API is unreachable
    console.log("Using local count fallback");
  }

  const logs = JSON.parse(localStorage.getItem("visitor_logs") || "[]");
  
  if (totalPageViews === 0) {
    totalPageViews = logs.length;
  }

  // Calculate unique visitor count from logs
  const uniqueVisitorIds = new Set(logs.map((log) => log.visitorId));
  
  // Device & Browser distributions
  const browserCounts = {};
  const deviceCounts = {};
  const osCounts = {};

  logs.forEach((log) => {
    browserCounts[log.browser] = (browserCounts[log.browser] || 0) + 1;
    deviceCounts[log.device] = (deviceCounts[log.device] || 0) + 1;
    osCounts[log.os] = (osCounts[log.os] || 0) + 1;
  });

  return {
    totalPageViews,
    uniqueVisitors: uniqueVisitorIds.size || (logs.length > 0 ? 1 : 0),
    logs,
    browserCounts,
    deviceCounts,
    osCounts,
  };
}

/**
 * Clears locally tracked stats
 */
export function clearVisitorLogs() {
  localStorage.removeItem("visitor_logs");
}
